import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X, ChevronDown } from "lucide-react";
import { ASSETS } from "@/lib/motion";
import { useBooking } from "@/components/bwg/BookingModal";

const NAV_LINKS = [
  { label: "Our Approach", to: "/our-approach" },
  { label: "About BWG", to: "/about-us" },
  { label: "Opportunities", children: [
      { label: "Ayodhya, Uttar Pradesh", to: "/ayodhya", status: "Live" },
      { label: "Dholera, Gujarat", status: "Live" },
      { label: "Sohna, Haryana", status: "Live" },
  ] },
  { label: "Featured On", to: "/press" },
  { label: "Careers", to: "/careers" },
];

export default function Nav({ overHero = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { open: openBooking } = useBooking();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  const solid = !overHero || scrolled;
  const textDark = solid;

  return (
    <>
      <motion.nav
        data-testid="main-nav"
        initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${solid ? "backdrop-blur-xl bg-brand-paper/85 border-b border-brand-ink/10" : "bg-transparent"}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <img src={textDark ? ASSETS.logoInk : ASSETS.logoWhite} alt="Bhaarat Wealth Group logo" className="w-8 h-8 object-contain transition-opacity duration-300 group-hover:opacity-80" />
            <span className={`font-display font-extrabold text-2xl tracking-tighter transition-colors ${textDark ? "text-brand-ink" : "text-brand-offwhite"}`}>
              BWG<span className="text-brand-orange">.</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => {
              const base = `text-sm font-medium transition-colors relative group ${textDark ? "text-brand-ink/70 hover:text-brand-ink" : "text-brand-offwhite/80 hover:text-brand-offwhite"}`;
              if (l.children) {
                return (
                  <div key={l.label} className="relative group/dd">
                    <button data-testid="nav-link-opportunities" className={`${base} inline-flex items-center gap-1`}>
                      {l.label}
                      <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover/dd:rotate-180" />
                    </button>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 invisible opacity-0 translate-y-1 group-hover/dd:visible group-hover/dd:opacity-100 group-hover/dd:translate-y-0 group-focus-within/dd:visible group-focus-within/dd:opacity-100 group-focus-within/dd:translate-y-0 transition-all duration-300">
                      <div className="w-72 bg-brand-offwhite border border-brand-ink/10 rounded-sm shadow-xl shadow-brand-ink/10 p-2">
                        {l.children.map((c) => c.to ? (
                          <Link key={c.label} to={c.to} data-testid={`nav-opp-${c.label.split(",")[0].toLowerCase()}`}
                            className="flex items-center justify-between px-4 py-3 rounded-sm hover:bg-brand-card transition-colors">
                            <span className="text-sm font-medium text-brand-ink">{c.label}</span>
                            <span className="text-[10px] uppercase tracking-wide text-brand-orange font-semibold">{c.status}</span>
                          </Link>
                        ) : (
                          <div key={c.label} className="flex items-center justify-between px-4 py-3 rounded-sm">
                            <span className="text-sm font-medium text-brand-ink">{c.label}</span>
                            <span className="text-[10px] uppercase tracking-wide text-brand-orange font-semibold">{c.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <Link key={l.label} to={l.to} data-testid={`nav-link-${l.label.split(" ").join("-").toLowerCase()}`}
                  className={`${base} ${pathname === l.to ? (textDark ? "text-brand-ink" : "text-brand-offwhite") : ""}`}>
                  {l.label}
                  <span className={`absolute -bottom-1 left-0 h-px bg-brand-orange transition-all duration-300 ${pathname === l.to ? "w-full" : "w-0 group-hover:w-full"}`} />
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <button type="button" onClick={() => openBooking("default", "nav_modal")} data-testid="nav-cta-button"
              className="hidden sm:inline-flex group items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-ink transition-colors duration-300">
              Book an Appointment
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(true)}
              className={`lg:hidden ${textDark ? "text-brand-ink" : "text-brand-offwhite"}`} aria-label="Open menu">
              <Menu className="w-7 h-7" />
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-brand-dark flex flex-col p-8" data-testid="mobile-menu">
            <div className="flex justify-between items-center mb-16">
              <span className="flex items-center gap-2.5">
                <img src={ASSETS.logoWhite} alt="Bhaarat Wealth Group logo" className="w-8 h-8 object-contain" />
                <span className="font-display font-extrabold text-2xl text-brand-offwhite">BWG<span className="text-brand-orange">.</span></span>
              </span>
              <button onClick={() => setOpen(false)} className="text-brand-offwhite" aria-label="Close menu"><X className="w-8 h-8" /></button>
            </div>
            <div className="flex flex-col gap-6">
              {NAV_LINKS.map((l, i) => (
                <motion.div key={l.label} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.06 }}>
                  {l.children ? (
                    <div>
                      <span className="font-display font-semibold text-4xl text-brand-offwhite/90">{l.label}</span>
                      <div className="mt-3 flex flex-col gap-2 pl-1">
                        {l.children.map((c) => c.to ? (
                          <Link key={c.label} to={c.to} onClick={() => setOpen(false)} className="text-lg text-brand-offwhite/70 hover:text-brand-orange transition-colors">{c.label}</Link>
                        ) : (
                          <span key={c.label} className="text-lg text-brand-offwhite/40">{c.label} · {c.status || "Live"}</span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link to={l.to} onClick={() => setOpen(false)}
                      className="font-display font-semibold text-4xl text-brand-offwhite hover:text-brand-orange transition-colors">
                      {l.label}
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>
            <button type="button" onClick={() => { setOpen(false); openBooking("default", "nav_modal"); }} data-testid="mobile-nav-cta-button"
              className="mt-auto inline-flex justify-center items-center gap-2 rounded-full bg-brand-orange px-6 py-4 font-semibold text-white">
              Book an Appointment <ArrowUpRight className="w-5 h-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
